import { useEffect, useState } from "react";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import Toggle from "../../components/shared/Toggle";
import { capitalize } from "../../utils/helpers";
import { useGetAIModels } from "../../api/queryHooks";
import Loader from "../../components/Loader/Loader";
import useModelPreferences from "../../hooks/useModelPreferences";
import { AnthropicIcon, GoogleIcon, OpenAIIcon } from "../../assets";
import { Info, Cpu, ChevronRight } from "lucide-react";
import EmptyState from "../../components/shared/EmptyState";

// Company icons and logos
const CompanyIcon = ({ company, className = "w-12 h-12" }) => {
  const companyLower = (company || "").toLowerCase();
  
  if (companyLower.includes('anthropic')) {
    return (
      <div className={`${className} rounded-xl flex items-center justify-center shadow-sm`}>
        <AnthropicIcon className="fill-ink-black dark:fill-ink-white w-7 h-7" />
      </div>
    );
  }
  
  if (companyLower.includes('openai')) {
    return (
      <div className={`${className} rounded-xl flex items-center justify-center shadow-sm`}>
        <OpenAIIcon className="fill-ink-black dark:fill-ink-white w-7 h-7" />
      </div>
    );
  }
  
  if (companyLower.includes('google')) {
    return (
      <div className={`${className} rounded-xl flex items-center justify-center shadow-sm`}>
        <GoogleIcon className="w-7 h-7" />
      </div>
    );
  }
  
  // Default fallback
  return (
    <div className={`${className} rounded-xl bg-fill-gray-500 flex items-center justify-center shadow-sm`}>
      <span className="text-ink-white font-bold text-xl">AI</span>
    </div>
  );
};

const ModelListingPage = () => {
  const { data: models, isLoading, error } = useGetAIModels();
  const { toggleModel, isModelEnabled, isDefaultModel, initializeModels } = useModelPreferences();
  const [selectedCategoryMap, setSelectedCategoryMap] = useState({}); // { [company]: 'All' | media_type }
  const [flippedMap, setFlippedMap] = useState({});

  useEffect(() => {
    if (models) {
      // Initialize preferences for new models
      initializeModels(models);
    }
  }, [models, initializeModels]);

  const handleToggleModel = (modelId, isEnabled) => {
    toggleModel(modelId, isEnabled);
  };

  const handleFlipCard = (modelId) => {
    setFlippedMap((prev) => ({ ...prev, [modelId]: !prev[modelId] }));
  };

  // Group models by company
  const groupModelsByCompany = (models) => {
    if (!models) return {};
    
    const grouped = models.reduce((acc, model) => {
      const company = model.company_name || model.provider || 'Other';
      if (!acc[company]) {
        acc[company] = [];
      }
      acc[company].push(model);
      return acc;
    }, {});

    // Sort models within each company
    Object.keys(grouped).forEach(company => {
      grouped[company].sort((a, b) => {
        const nameA = (a.display_name || a.name || a.model_name || '').toLowerCase();
        const nameB = (b.display_name || b.name || b.model_name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    });

    return grouped;
  };

  const groupedModels = groupModelsByCompany(models);

  const filterByCategory = (list, company) => {
    if (!list) return [];
    const sel = (selectedCategoryMap[company] || 'All');
    if (sel === 'All') return list;
    const key = sel.toLowerCase();
    return list.filter((m) => (m.media_type || '').toLowerCase() === key);
  };

  return (
    <SettingsPageShell title="Model Garden" description="Browse, enable, and configure the AI models available to your organisation, including model settings, access controls, and provider integrations." maxWidth="6xl">
      <div className="mt-0">

            {/* Category Tabs removed: per-company tabs shown in views when applicable */}

            {/* Loading state */}
            {isLoading && (
              <div className="py-6">
                <Loader variant="dashboard" />
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <p className="text-ink-red-500 dark:text-ink-red-400 mb-2">Failed to load models</p>
                  <p className="text-ink-gray-500 dark:text-ink-gray-400 text-sm">
                    Please try refreshing the page
                  </p>
                </div>
              </div>
            )}

            {/* Horizontal Slider View */}
            {models && models.length > 0 && (
              <div className="space-y-8">
              {Object.keys(groupedModels).sort().map((company) => (
                  <div key={company} className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CompanyIcon company={company} className="w-8 h-8" />
                        <h3 className="text-lg font-semibold text-ink-ink dark:text-ink-white">
                          {company === 'Hugging Face' ? 'Open models on Hugging Face' : `${company} models`}
                        </h3>
                      </div>
                    </div>
                    {/* Per-company tabs in slider (always show with default categories) */}
                    {(() => {
                      const companyModels = groupedModels[company] || [];
                      const typesSet = new Set(companyModels.map(m => (m.media_type || '').toLowerCase()).filter(Boolean));
                      // Always include default categories: All, Text, Image, Video, Audio
                      const defaultCategories = ['All', 'text', 'image', 'video', 'audio'];
                      const categories = defaultCategories.filter(cat => cat === 'All' || typesSet.has(cat));
                      // If only "All" exists, show all default categories anyway
                      const finalCategories = categories.length === 1 ? defaultCategories : categories;
                      const selectedCat = selectedCategoryMap[company] || 'text';
                      return (
                        <div className="flex items-center gap-2 mb-2">
                          {finalCategories.map((cat) => (
                            <button
                              key={cat}
                              onClick={() => setSelectedCategoryMap(prev => ({ ...prev, [company]: cat }))}
                              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${selectedCat === cat ? 'bg-fill-primary text-ink-on-primary border-edge-primary' : 'border-edge-gray-200 dark:border-edge-panel-border text-ink-ink dark:text-ink-white hover:bg-fill-gray-100 dark:hover:bg-fill-dialog-surface'}`}
                            >
                              {capitalize(cat)}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                    
                    {company === 'Hugging Face' && (
                      <p className="text-ink-gray-500 dark:text-ink-gray-400 text-sm mb-6">
                        Deploy some of the most popular open source models from Hugging Face to Vertex AI.
                      </p>
                    )}
                    
                    <div className="overflow-x-auto pb-4 show-scrollbar-on-hover">
                        <div className="flex gap-4" style={{ width: 'max-content' }}>
                          {filterByCategory(groupedModels[company], company).map((model) => (
                          <div
                            key={model.id}
                            className="bg-surface-card dark:bg-surface-card border border-edge-gray-200 dark:border-edge-panel-border rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 flex-shrink-0 w-[340px] h-40"
                            style={{ perspective: '1000px' }}
                          >
                            <div className="relative w-full h-full" style={{ height: '100%', width: '100%' }}>
                              <div className={`absolute inset-0 w-full h-full transition-transform duration-300 ${flippedMap[model.id] ? 'rotate-y-180' : ''}`} style={{ transformStyle: 'preserve-3d' }}>
                                {/* Front Side */}
                                <div className="w-full h-full flex flex-col justify-between" style={{ backfaceVisibility: 'hidden', position: 'absolute', inset: 0, zIndex: flippedMap[model.id] ? 1 : 2, transform: 'rotateY(0deg)' }}>
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <CompanyIcon company={company} className="w-7 h-7" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h4 className="font-medium text-ink-ink dark:text-ink-white truncate text-sm mb-1">
                                          {model.display_name || model.name || model.model_name || 'Unknown Model'}
                                        </h4>
                                        <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                                          {model.media_type}
                                        </p>
                                      </div>
                                    </div>
                                    
                                    <div className="flex-shrink-0 ml-2">
                                      <Toggle
                                        checked={isModelEnabled(model.id)}
                                        onChange={(v) => handleToggleModel(model.id, v)}
                                      />
                                    </div>
                                  </div>

                                  <div className="mb-3 h-8">
                                    <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 leading-relaxed line-clamp-2">
                                      {model.description ? (model.description.length > 80 ? model.description.slice(0, 80) + '...' : model.description) : ''}
                                    </p>
                                  </div>
                                  
                                  <div className="flex flex-wrap gap-1">
                                    {isDefaultModel(model.id) && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-fill-primary text-ink-on-primary">
                                        Default
                                      </span>
                                    )}
                                    {model.context_length && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-fill-indigo-100 text-ink-indigo-800 dark:bg-fill-hex-312e81 dark:text-ink-hex-a5b4fc">
                                        {model.context_length.toLocaleString()} tokens
                                      </span>
                                    )}
                                    {!isModelEnabled(model.id) && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-ink-red-600 border-edge-red-600 border dark:text-ink-red-600">
                                        Disabled
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex justify-end items-center">
                                    <button
                                      className="cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-primary focus-visible:ring-offset-1"
                                      onClick={() => handleFlipCard(model.id)}
                                      title="View details"
                                    >
                                      <Info className="w-5 h-5 text-ink-gray-500 hover:text-ink-primary transition-colors" />
                                    </button>
                                  </div>
                                </div>
                                {/* Back Side */}
                                <div className="w-full h-full relative rounded-xl p-4 bg-surface-card dark:bg-surface-card" style={{ backfaceVisibility: 'hidden', position: 'absolute', inset: 0, transform: 'rotateY(180deg)' }}>
                                  <div className="h-full flex flex-col justify-start items-start text-left">
                                    <h4 className="font-semibold text-ink-ink dark:text-ink-white mb-2">Model Details</h4>
                                    <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-2 leading-relaxed">
                                      {model.description || 'No details available.'}
                                    </p>
                                    <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                                      <span className="font-semibold text-ink-ink dark:text-ink-white">Created:</span> {model.created_at ? new Date(model.created_at).toLocaleDateString() : 'Unknown'}
                                    </p>
                                  </div>
                                  {/* Back icon in bottom right corner */}
                                  <button
                                    className="absolute right-1 w-6 h-6 rounded-full bg-fill-primary hover:bg-fill-primary-hover-deep-alt text-ink-on-primary flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-primary focus-visible:ring-offset-1"
                                    onClick={() => handleFlipCard(model.id)}
                                    title="Go back"
                                  >
                                    <ChevronRight className="w-4 h-4 rotate-180" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {models && models.length === 0 && !isLoading && !error && (
              <EmptyState
                icon={<Cpu className="w-7 h-7 text-ink-primary dark:text-ink-accent" />}
                title="No models available"
                description="Contact your administrator to add AI models to this organisation."
              />
            )}
          </div>
    </SettingsPageShell>
  );
};

export default ModelListingPage;